/* global Office */
import { DOMParser } from '@xmldom/xmldom'
import { UpdateItem, UpdateItemWithBody } from './UpdateItem'
import FindItem from './FindItem'
import GetItem, { GetItemToEmailAddress } from './GetItem'
import MoveItem from './MoveItem'
import { Recipients } from '../../types/loft'
import { addBreadcrumb, captureException } from '../../sentry-config'

const getSoapErrorMessage = (xmlBody: Document) => {
  const messageTextElement = xmlBody.getElementsByTagNameNS('*', 'MessageText')[0]
  const messageElement = xmlBody.getElementsByTagNameNS('*', 'Message')[0]
  const targetElement = messageTextElement || messageElement

  if (!targetElement) {
    return 'No error message'
  }

  const compactMessageMatches = String(targetElement.childNodes[0].nodeValue).match(/^[^.]+/)
  const compactMessage = compactMessageMatches && compactMessageMatches[0]

  if (!compactMessage) {
    return 'Empty error message'
  }

  const violationElement = xmlBody.getElementsByTagNameNS('*', 'Violation')[0]

  if (!violationElement) {
    return compactMessage.trim()
  }

  return `${compactMessage}: ${String(violationElement.childNodes[0].nodeValue)}`.trim()
}

/**
 * Wraps Office's makeEwsRequestAsync in a Promise and
 * handles two levels of errors: request and SOAP.
 * In case of success - converts SOAP response to DOM and resolves.
 * @return {Promise<Document, Error>} response
 */
export async function ewsRequest(body: string): Promise<Document> {
  const executor = (resolve: (value: Document) => void, reject: (value: string | null) => void) => {
    const requestCallback = ({ status, value, error, diagnostics }: Office.AsyncResult<string>) => {
      // @ts-expect-error
      if (status !== 'success' && status !== 'succeeded') {
        captureException(new Error(error.name || 'ewsRequest:status-failure'), {
          fingerprint: ['ewsRequest:status-failure'],
          extra: {
            diagnostics: JSON.stringify(diagnostics),
            message: error.message,
            code: error.code,
          },
        })
        // @ts-expect-error
        return reject(error)
      }

      try {
        const responseModel = new DOMParser().parseFromString(value, 'text/xml')
        const errorElement = responseModel.getElementsByTagNameNS('*', 'ResponseCode')[0]
        const isError = errorElement && errorElement.childNodes[0].nodeValue !== 'NoError'

        if (isError) {
          const errorName = String(errorElement.childNodes[0].nodeValue)
          switch (errorName) {
            case 'ErrorItemNotFound':
            case 'ErrorInvalidRecipients':
              break
            default:
              const errorMessage = getSoapErrorMessage(responseModel)
              captureException(new Error(errorMessage), {
                extra: {
                  name: errorName,
                },
                fingerprint: [`ewsRequest:${errorName}`],
              })
              break
          }

          return reject(errorElement.childNodes[0].nodeValue)
        }

        return resolve(responseModel)
      } catch (e) {
        captureException(e, {
          extra: {
            value,
          },
        })
      }
    }

    Office.context.mailbox.makeEwsRequestAsync(body, requestCallback)
  }

  return new Promise(executor)
}

export type Item = {
  id: null | string
  changeKey: null | string
  inReplyTo: null | string
  internetMessageId: null | string
  toEmailAddress: null | string
  attachments: {
    name: null | string
    contentType: null | string
    size: number
  }[]
}

/**
 * Creates simple object from DOM of SOAP response
 * to avoid boilerplate with DOM selectors.
 */
function extractItem(response: Document) {
  const item: Item = {
    id: null,
    changeKey: null,
    inReplyTo: null,
    internetMessageId: null,
    toEmailAddress: null,
    attachments: [],
  }

  const itemIdElement = response.getElementsByTagNameNS('*', 'ItemId')[0]
  if (itemIdElement) {
    item.id = itemIdElement.getAttribute('Id')
    item.changeKey = itemIdElement.getAttribute('ChangeKey')
  }

  const inReplyToElement = response.getElementsByTagNameNS('*', 'InReplyTo')[0]
  if (inReplyToElement) {
    item.inReplyTo = inReplyToElement.childNodes[0].nodeValue
  }

  const internetMessageIdElement = response.getElementsByTagNameNS('*', 'InternetMessageId')[0]
  if (internetMessageIdElement) {
    item.internetMessageId = internetMessageIdElement.childNodes[0].nodeValue
  }

  const toRecipientsElement = response.getElementsByTagNameNS('*', 'ToRecipients')[0]
  if (toRecipientsElement) {
    item.toEmailAddress = toRecipientsElement.getElementsByTagNameNS(
      '*',
      'EmailAddress'
    )[0].textContent
  }

  const attachmentsList = response.getElementsByTagNameNS('*', 'FileAttachment')
  for (let i = 0; i < attachmentsList.length; i += 1) {
    const attachmentNode = attachmentsList[i]
    item.attachments.push({
      name: attachmentNode.getElementsByTagNameNS('*', 'Name')[0].textContent,
      contentType: attachmentNode.getElementsByTagNameNS('*', 'ContentType')[0].textContent,
      size: parseInt(attachmentNode.getElementsByTagNameNS('*', 'Size')[0].textContent || '', 10),
    })
  }

  return item
}

/** Template tag to wrap SOAP requests body into envelope, header and else */
export function envelope(strings: string[], ...variables: string[]): string {
  const request = strings.reduce(
    (template, string, index) => template + string + (variables[index] || ''),
    ''
  )
  return `<?xml version="1.0" encoding="utf-8"?>
     <soap:Envelope
        xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xmlns:xsd="http://www.w3.org/2001/XMLSchema"
        xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages"
        xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
        xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
     >
       <soap:Header>
         <t:RequestServerVersion Version="Exchange2013" />
       </soap:Header>
       <soap:Body>${request}</soap:Body>
     </soap:Envelope>`
}

/**
 * In case of a reply, InReplyTo property contains Message-ID.
 * {@link https://en.wikipedia.org/wiki/Message-ID}
 */
export async function getItemById(id: string) {
  const request = GetItem(id)
  return extractItem(await ewsRequest(request))
}

/**
 * Ensure that current email is synced with exchange server
 */
export async function waitSyncAndGetItemById(
  id: string,
  attemptsLeft = 5,
  retryDelay = 3000
): Promise<null | Item> {
  try {
    addBreadcrumb({ message: 'EWS: GetItem', category: 'ews' })
    return await getItemById(id)
  } catch (error) {
    addBreadcrumb({
      message: `EWS: GetItem failed. Attempt left: ${attemptsLeft}`,
      level: 'error',
      category: 'ews',
    })
    if (attemptsLeft <= 1) {
      return null
    }
    await new Promise(resolve => setTimeout(resolve, retryDelay))
    return waitSyncAndGetItemById(id, attemptsLeft - 1, retryDelay)
  }
}

export async function getItemToEmailAddressById(id: string) {
  const request = GetItemToEmailAddress(id)
  const item = extractItem(await ewsRequest(request))
  return item.toEmailAddress
}

export async function findItemByInternetMessageId(id: string) {
  addBreadcrumb({ message: 'EWS: Searching item', category: 'ews' })
  const request = FindItem(id)
  return extractItem(await ewsRequest(request))
}

export async function updateAndSendItem(
  id: string,
  changeKey: string,
  body: string,
  subject: string,
  recipients: Recipients
) {
  addBreadcrumb({ message: 'EWS: Sending item', category: 'ews' })
  const newBody = ` <!DOCTYPE> <html><head></head><body>${body}</body></html> `

  return ewsRequest(UpdateItemWithBody(id, changeKey, newBody, subject, recipients))
}

export async function updateReply(id: string, changeKey: string) {
  addBreadcrumb({ message: 'EWS: Updating reply', category: 'ews' })
  return ewsRequest(UpdateItem(id, changeKey))
}

export async function moveItem(id: string, changeKey: string) {
  return ewsRequest(MoveItem(id, changeKey))
}

export async function updateParentEmail(inReplyTo: string) {
  if (!inReplyTo) {
    return
  }
  addBreadcrumb({ message: 'EWS: updating parent email', category: 'ews' })
  try {
    const letterAddressedBy: Item = await findItemByInternetMessageId(inReplyTo)
    if (!letterAddressedBy.id || !letterAddressedBy.changeKey) {
      addBreadcrumb({ message: 'EWS: Item not found', category: 'ews' })
      return
    }
    await updateReply(letterAddressedBy.id, letterAddressedBy.changeKey)
  } catch (error) {
    addBreadcrumb({
      category: 'ews',
      message: 'Loft: Failed to update parent',
      level: 'error',
      data: error,
    })
  }
}
